import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'compareList';
const MAX_COMPARE = 3;

const readList = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const writeList = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  // Broadcast within same tab (storage event only fires cross-tab)
  window.dispatchEvent(new CustomEvent('compareList:changed', { detail: list }));
};

export const useCompare = () => {
  const [list, setList] = useState(readList);

  useEffect(() => {
    const onExternal = () => setList(readList());
    window.addEventListener('storage', onExternal);
    window.addEventListener('compareList:changed', onExternal);
    return () => {
      window.removeEventListener('storage', onExternal);
      window.removeEventListener('compareList:changed', onExternal);
    };
  }, []);

  const has = useCallback((id) => list.some((v) => v.id === id), [list]);

  const add = useCallback((vehicle) => {
    const current = readList();
    if (current.some((v) => v.id === vehicle.id)) return { ok: false, reason: 'exists' };
    if (current.length >= MAX_COMPARE) return { ok: false, reason: 'full' };
    const next = [...current, vehicle];
    writeList(next);
    return { ok: true };
  }, []);

  const remove = useCallback((id) => {
    const next = readList().filter((v) => v.id !== id);
    writeList(next);
  }, []);

  const toggle = useCallback((vehicle) => {
    return has(vehicle.id) ? (remove(vehicle.id), { ok: true, removed: true }) : add(vehicle);
  }, [has, add, remove]);

  const clear = useCallback(() => writeList([]), []);

  return { list, count: list.length, max: MAX_COMPARE, has, add, remove, toggle, clear };
};
